'use client';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import { useQuickToast } from 'hooks/use-toast';
import { CatButton, CatIconShop } from '../../blocks-components';
import { useEsiteCtx } from 'services/esite/context';
import { useCoreContext } from 'providers/core-provider';

import {
  getFormattedItemData,
  getFormattedSearchSelectItem
} from 'components/product-comparison-grid/product-comparison-card/utils';
import { RelatedPartsPriceObject } from 'services/product/types';
import { ProductPriceAvailabilityWithDealerFluidAttributes } from 'services/priceAvailability/types';
import { ProductsPriceData } from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import { OrderItemPart } from 'services/cart/types';
import { REMAN_SELECT_ITEM } from 'app/[lang]/[esite]/(core)/product/[product]/constants/analytics-constants';
import { useProductDetailPageContext } from 'app/[lang]/[esite]/(core)/product/[product]/context/product-detail';
import { getPartNumberForAddToCart } from 'services/cart/utils';
import styles from './add-to-cart.module.scss';
import { Conditional } from '@cat-ecom/pcc-components';

type addToCartCTAProps = {
  gaprimaryCTA: string;
  gaBtnEventName: string;
  gaEventContext: string;
};

export interface AddtoCartProps {
  productPrice:
    | RelatedPartsPriceObject
    | ProductPriceAvailabilityWithDealerFluidAttributes;
  typeIcon?: boolean;
  fullWidth?: boolean;
  productPriceData?: ProductsPriceData;
  cartId?: string;
  locationId?: string;
  orderItem?: OrderItemPart | { orderItemId: string };
  size?: 'lg' | 'xs' | 'sm' | 'md';
  buttonLabel?: boolean;
  gaEventDetails?: addToCartCTAProps;
  currency?: string;
  itemListName?: string;
  itemListId?: string;
  isReplacedPartSection?: boolean;
  itemlistIdName?: string;
  classname?: string;
  isAddToCartMobile?: boolean;
}

export default function AddToCart({
  productPrice,
  typeIcon,
  fullWidth = true,
  productPriceData,
  cartId,
  size = 'sm',
  buttonLabel = false,
  gaEventDetails,
  currency,
  itemlistIdName,
  classname,
  isAddToCartMobile = false
}: Readonly<AddtoCartProps>) {
  const t = useTranslations();
  const { storeId, locale } = useEsiteCtx();
  const { serialNumber } = useProductDetailPageContext();
  const toast = useQuickToast();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const [addToCartButtonClicked, setAddToCartButtonClicked] = useState(false);
  const { isAddingToCart, handleAddToCart } = useCoreContext();

  const {
    partNumber = '',
    partName = productPrice?.name,
    quantity,
    sosCode,
    dealerFluidAttributes,
    isCatPart,
    partLevelFlags
  } = productPrice;

  const onClickHandler = async () => {
    const items = Array.isArray(productPrice)
      ? productPrice.map(
          ({
            partNumber,
            partName,
            quantity,
            sosCode,
            dealerFluidAttributes,
            isCatPart,
            partLevelFlags
          }) => ({
            partNumber: getPartNumberForAddToCart(partNumber, sosCode),
            partName,
            quantity,
            sosCode,
            // isCatPart is inside partLevelFlags when usign P&A v1
            isCatPart: isCatPart ?? partLevelFlags?.isCatPart,
            // we need use the dealer specific fluidPartNumber and override the sosCode if it exists
            ...(dealerFluidAttributes ? { ...dealerFluidAttributes } : {})
          })
        )
      : [
          {
            partNumber: getPartNumberForAddToCart(partNumber, sosCode),
            partName,
            quantity,
            sosCode,
            // isCatPart is inside partLevelFlags when usign P&A v1
            isCatPart: isCatPart ?? partLevelFlags?.isCatPart,
            // we need use the dealer specific fluidPartNumber and override the sosCode if it exists
            ...(dealerFluidAttributes ? { ...dealerFluidAttributes } : {})
          }
        ];
    const { error } = await handleAddToCart(
      {
        items
      },
      storeId,
      locale
    );
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR'),
        className: styles['cat-c-toast']
      });
    } else {
      toast.success({
        message: t('ITEM_ADDED_TO_CART'),
        className: styles['cat-c-toast']
      });
    }
    setAddToCartButtonClicked(true);
  };

  useEffect(() => {
    if (!isAddingToCart && addToCartButtonClicked) {
      fireSelectItemEvent({
        item: getFormattedSearchSelectItem(
          { orderItemId: cartId },
          productPriceData
        ),
        partName: `${productPriceData?.partNumber}: ${productPriceData?.name}`,
        locationId: storeId,
        itemSummary: productPriceData,
        isSearchPageEvent: true,
        itemListId: itemlistIdName ?? REMAN_SELECT_ITEM.item_list_id,
        itemListName: itemlistIdName ?? REMAN_SELECT_ITEM.item_list_name,
        serialNumber,
        index: productPriceData?.index ?? 1
      });

      addToCartItemEvent({
        locationId: storeId,
        itemSummary: productPriceData,
        itemData: getFormattedItemData(cartId, productPriceData, currency),
        itemListId: itemlistIdName,
        itemListName: itemlistIdName
      });

      setAddToCartButtonClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingToCart]);

  return (
    <>
      <Conditional test={typeIcon}>
        <CatButton
          variant="primary"
          size={size}
          hideText
          data-testid="cat-button"
          onClick={onClickHandler}
        >
          <CatIconShop slot="before"></CatIconShop>
        </CatButton>
      </Conditional>
      <Conditional test={isAddToCartMobile}>
        <CatButton
          size="md"
          onClick={onClickHandler}
          data-testid="reman-replace-mobile-add-to-cart-button"
        >
          {buttonLabel ? t('PM_ADD_ALL_TO_CART') : t('ADD')}
          <CatIconShop slot="after" />
        </CatButton>
      </Conditional>
      <Conditional test={!typeIcon && !isAddToCartMobile}>
        <CatButton
          variant="primary"
          size={size}
          fullWidth={fullWidth}
          onClick={onClickHandler}
          data-track-event={gaEventDetails?.gaprimaryCTA}
          data-track-attr-nltext={gaEventDetails?.gaBtnEventName}
          data-track-attr-context={gaEventDetails?.gaEventContext}
        >
          <span className={classname}>
            {buttonLabel ? t('PM_ADD_ALL_TO_CART') : t('ADD_TO_CART')}
          </span>
        </CatButton>
      </Conditional>
    </>
  );
}
